import { Injectable } from '@angular/core';
import { AvailableField, AvailableFields } from '../interface';
import { FieldType as CrmFieldType, PagedRequestOptionsInterface } from '@vendasta/crm';
import { EMPTY, expand, forkJoin, from, map, mergeMap, Observable, of, reduce, switchMap, take, toArray } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { FieldType, MappedField } from '@vendasta/forms_microservice';
import {
  FormsApiService,
  ListMappedFieldSchemaResponseInterface,
  MappedFieldInterface,
} from '@vendasta/forms_microservice';

const MAX_SERVER_PAGE_SIZE = 200;

type SupportedMappedResource = 'crm_contact' | 'crm_company' | 'utm_attribute';

@Injectable({ providedIn: 'root' })
export class MappedFieldService {
  constructor(private readonly formApiService: FormsApiService) {}

  namespace$: Observable<string>;

  set namespace(ns: Observable<string>) {
    this.namespace$ = ns;
  }

  buildMappedFields(search: string): Observable<AvailableFields[]> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return forkJoin([
          // --
          this.listAllMappedFields$(namespace, 'crm_contact').pipe(
            map((fieldSchemas) => {
              return {
                labelKey: 'Contact fields',
                fields: fieldSchemas.filter((field) => field.fieldName?.toLowerCase().includes(search.toLowerCase())),
              } as AvailableFields;
            }),
            catchError((error) => {
              console.error('Error loading contact fields', error);
              return of(null);
            }),
          ),
          this.listAllMappedFields$(namespace, 'crm_company').pipe(
            map((fieldSchemas) => {
              return {
                labelKey: 'Company fields',
                fields: fieldSchemas.filter((field) => field.fieldName?.toLowerCase().includes(search.toLowerCase())),
              } as AvailableFields;
            }),
            catchError((error) => {
              console.error('Error loading company fields', error);
              return of(null);
            }),
          ),
          this.listAllMappedFields$(namespace, 'utm_attribute').pipe(
            map((fieldSchemas) => {
              return {
                labelKey: 'UTM attribute fields',
                fields: fieldSchemas.filter((field) => field.fieldName?.toLowerCase().includes(search.toLowerCase())),
                hiddenGroup: fieldSchemas.every((field) => {
                  return (field.formField as MappedField).systemDefined?.hidden;
                }),
              } as AvailableFields;
            }),
            catchError((error) => {
              console.error('Error loading utm fields', error);
              return of(null);
            }),
          ),
        ]);
      }),
      map(([contactFields, companyFields, utmFields]) => {
        const allFields = [];
        if (utmFields) {
          allFields.unshift(utmFields);
        }
        if (companyFields) {
          allFields.unshift(companyFields);
        }
        if (contactFields) {
          allFields.unshift(contactFields);
        }
        return allFields;
      }),
    );
  }

  listAllMappedFields$(namespace: string, mappedToResource: SupportedMappedResource): Observable<AvailableField[]> {
    return this.listAllMappedToResourceFields$(namespace, mappedToResource).pipe(
      mergeMap((fieldSchemaArray) =>
        from(fieldSchemaArray).pipe(
          filter((fieldSchema) => !fieldSchema.readonly),
          map((fieldSchema: MappedFieldInterface) => {
            return {
              fieldName: fieldSchema.name,
              formField: {
                id: fieldSchema.id,
                type: fieldSchema.type,
                mappedTo: fieldSchema.mappedTo,
                systemDefined: fieldSchema.systemDefined,
              },
            } as AvailableField;
          }),
          toArray(),
        ),
      ),
    );
  }

  listAllMappedToResourceFields$(
    namespace: string,
    mappedToResource: SupportedMappedResource,
  ): Observable<MappedFieldInterface[]> {
    return of({
      fieldSchemas: [],
      pagingMetadata: {
        nextCursor: '',
        hasMore: true,
      },
    }).pipe(
      expand((resp: ListMappedFieldSchemaResponseInterface) => {
        if (!resp?.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.listObjectFieldsNextPage$(namespace, mappedToResource, {
          cursor: resp.pagingMetadata.nextCursor,
          pageSize: MAX_SERVER_PAGE_SIZE,
        });
      }),
      reduce((acc: MappedFieldInterface[], val) => {
        return [...acc, ...(val?.fieldSchemas || [])];
      }, []),
    );
  }

  listObjectFieldsNextPage$(
    namespace: string,
    mappedToResource: string,
    paging: PagedRequestOptionsInterface,
  ): Observable<ListMappedFieldSchemaResponseInterface> {
    return this.formApiService.listMappedFieldSchema({
      namespace: namespace,
      mappedToResource: mappedToResource,
      pagingOptions: paging,
    });
  }

  convertFieldSchemaTypeToFormFieldType(fieldSchemaType: CrmFieldType): FieldType {
    switch (fieldSchemaType) {
      case CrmFieldType.FIELD_TYPE_INTEGER:
        return FieldType.FIELD_TYPE_INTEGER;
      case CrmFieldType.FIELD_TYPE_CURRENCY:
        return FieldType.FIELD_TYPE_CURRENCY;
      case CrmFieldType.FIELD_TYPE_PHONE:
        return FieldType.FIELD_TYPE_PHONE;
      case CrmFieldType.FIELD_TYPE_EMAIL:
        return FieldType.FIELD_TYPE_EMAIL;
      case CrmFieldType.FIELD_TYPE_STRING:
      case CrmFieldType.FIELD_TYPE_GEOPOINT:
      case CrmFieldType.FIELD_TYPE_STRING_LIST:
      case CrmFieldType.FIELD_TYPE_TAG:
        return FieldType.FIELD_TYPE_STRING;
      case CrmFieldType.FIELD_TYPE_BOOLEAN:
        return FieldType.FIELD_TYPE_BOOLEAN;
      case CrmFieldType.FIELD_TYPE_DATETIME:
      case CrmFieldType.FIELD_TYPE_DATE:
        return FieldType.FIELD_TYPE_DATE;
      case CrmFieldType.FIELD_TYPE_DROPDOWN:
        return FieldType.FIELD_TYPE_DROPDOWN;
      default:
        return FieldType.FIELD_TYPE_INVALID;
    }
  }
}
